"use client";

import { c as _c } from "react/compiler-runtime";
import type { Genre, Premiere } from "@filmweb/sanity-types";
import { format } from "date-fns";
import { Suspense } from "react";
import ShowsForDate, { type MovieSearchParams } from "src/components/film/program/ShowsForDate";
import LoadingIndicator from "src/components/loadingindicator/LoadingIndicator";
import NoLocation from "src/components/program/NoLocation";
import ProgramError from "src/components/program/ProgramError";
import QueryErrorBoundary from "src/components/queryerrorboundary/QueryErrorBoundary";
import { useLocationContext, useProgramDatesContext } from "src/lib/contexts/ContextTypes";
import { SHOWSFORDATE_BY_ID_QUERY, SHOWSFORDATE_BY_TITLE_QUERY } from "src/lib/program/programQueries";
import { useProgramLocationParams } from "src/lib/program/useProgramLocationParams";
import { useShowCinemaFilter } from "src/lib/program/useShowCinemaFilter";

//#region [Props]
type ProgramShowsProps = {
  /** the movie to show the shows for: by id, or by title for movies without an id yet */
  movieParams: MovieSearchParams;
  edi?: string | null;
  movieTitle: string;
  premiere?: Premiere | null;
  genres?: Genre[];
  isKinoklubbMovie: boolean;
  isAdFree?: boolean;
  adFreeSkin?: string | null;
};
//#endregion

//#region [Component]
/** The shows on the selected date. Reads the url (?firm=, ?theatre=), so it must be inside <Suspense> */
export default function ProgramShows(t0) {
  const $ = _c(25);
  const {
    movieParams,
    edi,
    movieTitle,
    premiere,
    genres,
    isKinoklubbMovie,
    isAdFree,
    adFreeSkin
  } = t0;
  const locationContext = useLocationContext();
  const {
    selectedDate,
    showingAllDates
  } = useProgramDatesContext();
  const {
    locationParams,
    atLeastOneLocationSelected
  } = useProgramLocationParams(isAdFree, adFreeSkin);
  const showCinemaFilter = useShowCinemaFilter(isAdFree, adFreeSkin);
  let t1;
  let t2;
  let t3;
  if ($[0] !== atLeastOneLocationSelected || $[1] !== edi || $[2] !== genres || $[3] !== isKinoklubbMovie || $[4] !== locationContext.isReady || $[5] !== locationParams || $[6] !== movieParams || $[7] !== movieTitle || $[8] !== premiere || $[9] !== selectedDate || $[10] !== showCinemaFilter || $[11] !== showingAllDates) {
    const showsVariables = {
      ...locationParams,
      ...movieParams,
      date: showingAllDates || !selectedDate ? null : format(selectedDate, "yyyy-MM-dd")
    };
    const query = movieParams.movieTitle ? SHOWSFORDATE_BY_TITLE_QUERY : SHOWSFORDATE_BY_ID_QUERY;
    if ($[15] !== atLeastOneLocationSelected || $[16] !== locationContext.isReady) {
      t1 = !locationContext.isReady && !atLeastOneLocationSelected && <LoadingIndicator />;
      $[15] = atLeastOneLocationSelected;
      $[16] = locationContext.isReady;
      $[17] = t1;
    } else {
      t1 = $[17];
    }
    if ($[18] !== atLeastOneLocationSelected || $[19] !== locationContext.isReady) {
      t2 = locationContext.isReady && !atLeastOneLocationSelected && <NoLocation />;
      $[18] = atLeastOneLocationSelected;
      $[19] = locationContext.isReady;
      $[20] = t2;
    } else {
      t2 = $[20];
    }
    t3 = atLeastOneLocationSelected && <QueryErrorBoundary key={JSON.stringify(showsVariables)} context="program/shows" fallback={_temp}><Suspense fallback={<LoadingIndicator />}><ShowsForDate edi={edi} query={query} variables={showsVariables} showCinemaFilter={showCinemaFilter} movieTitle={movieTitle} premiere={premiere} genres={genres} isKinoklubbMovie={isKinoklubbMovie} /></Suspense></QueryErrorBoundary>;
    $[0] = atLeastOneLocationSelected;
    $[1] = edi;
    $[2] = genres;
    $[3] = isKinoklubbMovie;
    $[4] = locationContext.isReady;
    $[5] = locationParams;
    $[6] = movieParams;
    $[7] = movieTitle;
    $[8] = premiere;
    $[9] = selectedDate;
    $[10] = showCinemaFilter;
    $[11] = showingAllDates;
    $[12] = t1;
    $[13] = t2;
    $[14] = t3;
  } else {
    t1 = $[12];
    t2 = $[13];
    t3 = $[14];
  }
  let t4;
  if ($[21] !== t1 || $[22] !== t2 || $[23] !== t3) {
    t4 = <>{t1}{t2}{t3}</>;
    $[21] = t1;
    $[22] = t2;
    $[23] = t3;
    $[24] = t4;
  } else {
    t4 = $[24];
  }
  return t4;
}
//#endregion
function _temp(retry) {
  return <ProgramError onRetry={retry} />;
}